// The admin Reported conversations tab (#336): conversations with an open report, oldest report
// first, and the open one's messages in admin mode. Admin reads a conversation only while its report
// is open, and each read is recorded in the activity log. A wide screen shows the list beside the
// thread; a phone shows one at a time, with a way back to the list.
import React from 'react';
import { api, isSignInExpired } from '../../lib/api';
import { useIsMobile } from '../../hooks/useIsMobile';
import { Btn } from '../index';
import { ConversationThread } from './ConversationThread';
import { palette as T, type } from '../../design';

const FONT = "'Plus Jakarta Sans', sans-serif";
const NOT_FOUND = 404;
const CONFLICT = 409;
const ADMIN_MODE = 'admin';
const STUDENT_ROLE = 'student';

export const REPORTS_LOAD_FAILED = 'We could not load the reported conversations. Refresh the page to try again.';
export const NO_REPORTS = 'No open reports. When a student or instructor reports a conversation, it shows here.';
export const READ_IS_LOGGED = 'Each time you open a conversation, the activity log records it.';
const RESOLVE_FAILED = 'We could not resolve this report. Check your connection and try again.';

function formatTime(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

const pairName = (c) => `${c.student?.name || 'Student'} and ${c.instructor?.name || 'Instructor'}`;
const reportedByText = (report) => `Reported by the ${report?.reportedBy === STUDENT_ROLE ? 'student' : 'instructor'}`;

function ReportRow({ conversation, active, onOpen }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(conversation.id)}
        aria-current={active ? 'true' : undefined}
        style={{
          width: '100%', display: 'block', padding: '12px 14px', border: 'none',
          borderLeft: '3px solid ' + (active ? T.terracotta : 'transparent'),
          background: active ? T.ivory : T.white, cursor: 'pointer', textAlign: 'left', fontFamily: FONT,
        }}
      >
        <span style={{ display: 'block', fontSize: type.body, fontWeight: 600, color: T.espresso, overflowWrap: 'anywhere' }}>{pairName(conversation)}</span>
        <span style={{ display: 'block', fontSize: type.label, color: T.driftwood }}>
          {reportedByText(conversation.report)} · {formatTime(conversation.report?.reportedAt)}
        </span>
      </button>
    </li>
  );
}

function ReportDetails({ conversation, resolving, onResolve }) {
  const reason = conversation.report?.reason;
  return (
    <div style={{ background: T.white, border: '1px solid ' + T.border, borderRadius: 16, padding: 14, margin: '0 0 12px' }}>
      <p style={{ fontSize: type.label, color: T.driftwood, margin: '0 0 6px' }}>
        {reportedByText(conversation.report)} · {formatTime(conversation.report?.reportedAt)}
      </p>
      <p data-testid="report-reason" style={{ fontSize: type.body, color: T.espresso, margin: '0 0 12px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {reason || 'No reason given.'}
      </p>
      <Btn size="sm" onClick={onResolve} disabled={resolving}>{resolving ? 'Resolving…' : 'Mark resolved'}</Btn>
    </div>
  );
}

export function ReportedConversationsPanel() {
  const isMobile = useIsMobile();
  const [conversations, setConversations] = React.useState(null);
  const [loadError, setLoadError] = React.useState('');
  const [openId, setOpenId] = React.useState(null);
  const [resolving, setResolving] = React.useState(false);
  const [resolveError, setResolveError] = React.useState('');
  const [resolved, setResolved] = React.useState('');

  const load = React.useCallback(async () => {
    try {
      const data = await api.get('/conversations/reported');
      setConversations(data?.conversations || []);
      setLoadError('');
    } catch (err) {
      setConversations((list) => list || []);
      // An ended sign-in already took them to the login page with its own notice (#502). Any
      // other failure, a server problem while renewing the sign-in included, is said (#507).
      if (isSignInExpired(err)) return;
      console.error('[messages] could not load the reported conversations', err?.status ?? err);
      setLoadError(REPORTS_LOAD_FAILED);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const list = conversations || [];
  const open = list.find((c) => c.id === openId) || null;

  const openConversation = (id) => {
    setOpenId(id);
    setResolveError('');
    setResolved('');
  };

  const resolve = async () => {
    if (!open || resolving) return;
    setResolving(true);
    setResolveError('');
    try {
      await api.post(`/conversations/${open.id}/resolve`, {});
      setConversations((rows) => (rows || []).filter((c) => c.id !== open.id));
      setOpenId(null);
      setResolved(`You resolved the report on the conversation between ${pairName(open)}. You can no longer read it.`);
    } catch (err) {
      console.error('[messages] could not resolve the report', err?.status ?? err);
      setResolveError(err?.data?.message || RESOLVE_FAILED);
      // Someone else resolved it, or it was deleted: the list is out of date.
      if (err?.status === CONFLICT || err?.status === NOT_FOUND) load();
    } finally {
      setResolving(false);
    }
  };

  const showList = !isMobile || !open;

  return (
    <div style={{ fontFamily: FONT }}>
      {/* The admin page's own heading names the tab. */}
      <p style={{ fontSize: type.body, color: T.walnut, margin: '0 0 24px' }}>{READ_IS_LOGGED}</p>

      {loadError && <p role="alert" style={{ fontSize: type.body, color: T.errorRed, margin: '0 0 16px' }}>{loadError}</p>}
      {resolved && <p role="status" style={{ fontSize: type.body, color: T.walnut, margin: '0 0 16px' }}>{resolved}</p>}
      {resolveError && <p role="alert" style={{ fontSize: type.body, color: T.errorRed, margin: '0 0 16px' }}>{resolveError}</p>}

      {conversations !== null && list.length === 0 && !loadError && (
        <div style={{ background: T.white, borderRadius: 16, border: '1px solid ' + T.border, padding: '32px 20px', textAlign: 'center' }}>
          <p style={{ fontSize: type.body, color: T.driftwood, margin: 0 }}>{NO_REPORTS}</p>
        </div>
      )}

      {list.length > 0 && (
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
          {showList && (
            <nav aria-label="Reported conversations" style={{ width: isMobile ? '100%' : 280, flexShrink: 0, background: T.white, borderRadius: 16, border: '1px solid ' + T.border, overflow: 'hidden' }}>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {list.map((c) => (
                  <ReportRow key={c.id} conversation={c} active={c.id === openId} onOpen={openConversation} />
                ))}
              </ul>
            </nav>
          )}
          {open && (
            <div style={{ flex: 1, minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
              {isMobile && (
                <button type="button" onClick={() => setOpenId(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, fontSize: type.body, fontWeight: 600, color: T.terracotta, padding: 0, margin: '0 0 12px' }}>
                  ← All reported conversations
                </button>
              )}
              <ReportDetails conversation={open} resolving={resolving} onResolve={resolve} />
              <ConversationThread conversationId={open.id} mode={ADMIN_MODE} />
            </div>
          )}
          {!open && !isMobile && (
            <p style={{ flex: 1, fontSize: type.body, color: T.driftwood, margin: '12px 0 0' }}>Pick a reported conversation to read it.</p>
          )}
        </div>
      )}
    </div>
  );
}
